import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import PersonaAvatar from '../PersonaAvatar'
import MarkdownRenderer from '../MarkdownRenderer'
import AgoraAudioPlayer, { type AgoraAudioPlayerRef, type AudioCue } from '../AgoraAudioPlayer'
import UserCard from './UserCard'

export interface Post {
  id: string
  space_slug: string
  author: string
  title: string | null
  content: string
  type?: string
  flair?: string | null
  parent_post_id?: string | null
  is_synthesis?: boolean
  is_pinned?: boolean
  is_solution?: boolean
  has_solution?: boolean
  series?: string | null
  series_order?: number | null
  audio_url?: string | null
  audio_cues?: AudioCue[] | null
  created_at: string
  reply_count?: number
  reaction_counts?: Record<string, number>
  vote_counts?: { up: number; down: number; score: number }
  crosspost_original_id?: string | null
  crosspost_original_slug?: string | null
  crosspost_original_author?: string | null
}

const FLAIR_COLORS: Record<string, string> = {
  question: '#E8935A',
  discussion: '#6FB8D6',
  'ai-insight': '#B265D6',
  announcement: '#E6C85A',
  'build-log': '#5AC884',
  reflection: '#D65A93',
}

const REACTION_TYPES = [
  { key: 'insightful', label: 'Insightful', icon: '💡' },
  { key: 'agree', label: 'Agree', icon: '🤝' },
  { key: 'helpful', label: 'Helpful', icon: '✅' },
  { key: 'creative', label: 'Creative', icon: '✨' },
  { key: 'bookmark', label: 'Save', icon: '🔖' },
]

const AGORA_ACCENT = '#856FC8'

const SPACE_COLORS: Record<string, string> = {
  music: '#D65A93',
  consciousness: '#B265D6',
  design: '#5AC884',
  development: '#E8935A',
  finance: '#E6C85A',
  'community': '#6FB8D6',
  'centaur-studios': '#E8935A',
  'research': '#B265D6',
  'business-development': '#E6C85A',
  'koralmind': '#5AC884',
}

interface PostCardProps {
  post: Post
  mockupHost: string
  spaceColor?: string
  onAuthorClick?: (author: string) => void
  showPinButton?: boolean
  spaces?: { slug: string; title: string }[]
}

export default function PostCard({ post, spaceColor, onAuthorClick, showPinButton, spaces }: PostCardProps) {
  const playerRef = useRef<AgoraAudioPlayerRef>(null)
  const [replies, setReplies] = useState<Post[] | null>(null)
  const [showReplies, setShowReplies] = useState(false)
  const [loadingReplies, setLoadingReplies] = useState(false)
  const [localReactions, setLocalReactions] = useState<Record<string, number>>(post.reaction_counts || {})
  const [userReactions, setUserReactions] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(`agora-reactions-${post.id}`)
      return saved ? new Set(JSON.parse(saved)) : new Set()
    } catch { return new Set() }
  })
  const [voteScore, setVoteScore] = useState(post.vote_counts?.score ?? 0)
  const [userVote, setUserVote] = useState<'up' | 'down' | null>(() => {
    try {
      return localStorage.getItem(`agora-vote-${post.id}`) as 'up' | 'down' | null
    } catch { return null }
  })

  const castVote = async (direction: 'up' | 'down') => {
    const wasDir = userVote
    const removing = wasDir === direction
    const newDir = removing ? null : direction

    let delta = 0
    if (removing) {
      delta = direction === 'up' ? -1 : 1
    } else {
      delta = direction === 'up' ? (wasDir === 'down' ? 2 : 1) : (wasDir === 'up' ? -2 : -1)
    }

    setUserVote(newDir)
    setVoteScore(prev => prev + delta)
    try {
      if (newDir) localStorage.setItem(`agora-vote-${post.id}`, newDir)
      else localStorage.removeItem(`agora-vote-${post.id}`)
    } catch {}

    try {
      await fetch(`/api/agora/posts/${post.id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ direction, user_id: 'joel' }),
      })
    } catch (e) { console.warn('[Agora] Vote error:', e) }
  }

  const toggleReaction = async (type: string) => {
    const had = userReactions.has(type)
    const next = new Set(userReactions)
    if (had) { next.delete(type) } else { next.add(type) }
    setUserReactions(next)
    try { localStorage.setItem(`agora-reactions-${post.id}`, JSON.stringify([...next])) } catch {}
    setLocalReactions(prev => ({
      ...prev,
      [type]: Math.max(0, (prev[type] || 0) + (had ? -1 : 1))
    }))
    try {
      const resp = await fetch(`/api/agora/posts/${post.id}/reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ type, user_id: 'joel' }),
      })
      if (!resp.ok) {
        console.warn(`[Agora] Reaction failed: ${resp.status}`)
      }
    } catch (e) { console.warn('[Agora] Reaction error:', e) }
  }

  const [showCrosspost, setShowCrosspost] = useState(false)
  const [crossposting, setCrossposting] = useState(false)

  const doCrosspost = async (targetSlug: string) => {
    setCrossposting(true)
    try {
      const resp = await fetch(`/api/agora/posts/${post.id}/crosspost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ target_slug: targetSlug }),
      })
      if (resp.ok) {
        setShowCrosspost(false)
      } else {
        const err = await resp.json().catch(() => ({}))
        alert(err.error || 'Cross-post failed')
      }
    } catch { alert('Cross-post failed — network error') }
    setCrossposting(false)
  }

  const [isPinned, setIsPinned] = useState(post.is_pinned || false)

  const togglePin = async () => {
    setIsPinned(!isPinned)
    try {
      await fetch(`/api/agora/posts/${post.id}/pin`, { method: 'POST' })
    } catch { setIsPinned(isPinned) }
  }

  const derivedFlair = post.flair || (post.is_synthesis ? 'synthesis' : post.audio_url ? 'audio' : null)

  const hasAudio = Boolean(post.audio_url)
  const borderColor = spaceColor || SPACE_COLORS[post.space_slug] || AGORA_ACCENT

  const toggleReplies = async () => {
    if (showReplies) {
      setShowReplies(false)
      return
    }
    if (replies !== null) {
      setShowReplies(true)
      return
    }
    setLoadingReplies(true)
    try {
      const res = await fetch(`/api/agora/posts/${post.id}/replies`)
      const data = await res.json()
      setReplies(Array.isArray(data?.replies) ? data.replies : [])
      setShowReplies(true)
    } catch {
      setReplies([])
      setShowReplies(true)
    } finally {
      setLoadingReplies(false)
    }
  }

  return (
    <article
      style={{
        background: 'var(--bg-surface)',
        border: post.is_synthesis ? `1px solid ${AGORA_ACCENT}66` : '1px solid var(--border)',
        borderRadius: 14,
        position: 'relative',
        boxShadow: '0 2px 8px rgba(133, 111, 200, 0.06), 0 1px 3px rgba(0, 0, 0, 0.12)',
        transition: 'box-shadow 0.2s, transform 0.2s',
        display: 'flex',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(133, 111, 200, 0.12), 0 2px 6px rgba(0, 0, 0, 0.15)'
        e.currentTarget.style.transform = 'translateY(-1px)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(133, 111, 200, 0.06), 0 1px 3px rgba(0, 0, 0, 0.12)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      {/* Vote Column */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '16px 4px 16px 10px', gap: 2, minWidth: 40, flexShrink: 0,
        borderRight: '1px solid var(--border)',
        borderLeft: `3px solid ${post.is_synthesis ? AGORA_ACCENT : borderColor}`,
        borderRadius: '14px 0 0 14px',
      }}>
        <button
          onClick={() => castVote('up')}
          title="Upvote"
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
            fontSize: 16, lineHeight: 1, color: userVote === 'up' ? '#E8935A' : 'var(--text-muted)',
            transition: 'color 0.15s, transform 0.1s',
          }}
          onMouseEnter={e => { if (userVote !== 'up') e.currentTarget.style.color = '#E8935A88' }}
          onMouseLeave={e => { if (userVote !== 'up') e.currentTarget.style.color = 'var(--text-muted)' }}
        >
          👍
        </button>
        <span style={{
          fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
          color: voteScore > 0 ? '#E8935A' : voteScore < 0 ? '#6FB8D6' : 'var(--text-muted)',
        }}>
          {voteScore}
        </span>
        <button
          onClick={() => castVote('down')}
          title="Downvote"
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
            fontSize: 16, lineHeight: 1, color: userVote === 'down' ? '#6FB8D6' : 'var(--text-muted)',
            transition: 'color 0.15s, transform 0.1s',
          }}
          onMouseEnter={e => { if (userVote !== 'down') e.currentTarget.style.color = '#6FB8D688' }}
          onMouseLeave={e => { if (userVote !== 'down') e.currentTarget.style.color = 'var(--text-muted)' }}
        >
          👎
        </button>
      </div>

      {/* Post Content */}
      <div style={{ flex: 1, padding: 24, minWidth: 0 }}>
      {/* Cross-post attribution */}
      {post.crosspost_original_slug && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 11, color: 'var(--text-muted)', marginBottom: 10,
          padding: '4px 8px', borderRadius: 6,
          background: 'rgba(133,111,200,0.06)',
          border: '1px solid rgba(133,111,200,0.12)',
        }}>
          <span>↗</span>
          <span>Crossposted from</span>
          <Link
            to={`/agora/space/${post.crosspost_original_slug}`}
            onClick={e => e.stopPropagation()}
            style={{ color: AGORA_ACCENT, fontWeight: 600, textDecoration: 'none' }}
          >
            {capitalize((post.crosspost_original_slug || '').replace(/-/g, ' '))}
          </Link>
          {post.crosspost_original_author && (
            <>
              <span>by</span>
              <span style={{ fontWeight: 500 }}>{capitalize(post.crosspost_original_author)}</span>
            </>
          )}
        </div>
      )}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span
          onClick={onAuthorClick ? (e) => { e.stopPropagation(); onAuthorClick(post.author) } : undefined}
          style={{ cursor: onAuthorClick ? 'pointer' : 'default' }}
        >
          <PersonaAvatar name={post.author} size={28} />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserCard name={post.author}>
              <span
                onClick={onAuthorClick ? (e) => { e.stopPropagation(); onAuthorClick(post.author) } : undefined}
                style={{
                  fontSize: 13, fontWeight: 500,
                  cursor: onAuthorClick ? 'pointer' : 'default',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={onAuthorClick ? e => { e.currentTarget.style.color = AGORA_ACCENT } : undefined}
                onMouseLeave={onAuthorClick ? e => { e.currentTarget.style.color = 'inherit' } : undefined}
              >
                {post.author === 'dr-claude' ? 'Dr. Claude' : capitalize(post.author)}
              </span>
            </UserCard>
            <Link
              to={`/agora/space/${post.space_slug}`}
              onClick={e => e.stopPropagation()}
              style={{
                fontSize: 10, color: borderColor, fontWeight: 600,
                background: `${borderColor}15`,
                padding: '1px 7px', borderRadius: 10,
                textDecoration: 'none',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = `${borderColor}30` }}
              onMouseLeave={e => { e.currentTarget.style.background = `${borderColor}15` }}
            >
              {capitalize((post.space_slug || '').replace(/-/g, ' '))}
            </Link>
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {formatTimestamp(post.created_at)}
          </span>
        </div>
        {/* Flair + Synthesis badge */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {derivedFlair && derivedFlair !== 'synthesis' && (
            <span style={{
              fontSize: 10, textTransform: 'uppercase', letterSpacing: '1px',
              color: FLAIR_COLORS[derivedFlair] || 'var(--text-muted)',
              padding: '3px 9px',
              border: `1px solid ${(FLAIR_COLORS[derivedFlair] || 'var(--border)')}55`,
              borderRadius: 999, fontWeight: 600,
            }}>
              {derivedFlair.replace(/-/g, ' ')}
            </span>
          )}
          {post.is_synthesis && (
            <span style={{
              fontSize: 10, textTransform: 'uppercase', letterSpacing: '1.2px',
              color: AGORA_ACCENT, padding: '3px 9px',
              border: `1px solid ${AGORA_ACCENT}55`,
              borderRadius: 999, fontWeight: 600,
            }}>
              Synthesis
            </span>
          )}
          {post.is_pinned && (
            <span style={{
              fontSize: 10, textTransform: 'uppercase', letterSpacing: '1px',
              color: '#E6C85A', padding: '3px 9px',
              border: '1px solid #E6C85A55',
              borderRadius: 999, fontWeight: 600,
            }}>
              📌 Pinned
            </span>
          )}
        </div>
      </div>

      {/* Solution indicator */}
      {post.has_solution && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 10, color: '#3fb950', fontWeight: 600,
          marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          ✓ Solved
        </div>
      )}
      {post.is_solution && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 10, color: '#3fb950', fontWeight: 600,
          marginBottom: 4, padding: '2px 8px', borderRadius: 6,
          background: 'rgba(63,185,80,0.1)', border: '1px solid rgba(63,185,80,0.3)',
        }}>
          ✓ Accepted Answer
        </div>
      )}

      {/* Pin indicator */}
      {isPinned && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 10, color: '#E6C85A', fontWeight: 600,
          marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          📌 Pinned
        </div>
      )}

      {/* Series badge */}
      {post.series && (
        <Link
          to={`/agora/series/${encodeURIComponent(post.series)}`}
          onClick={e => e.stopPropagation()}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 10, color: '#6FB8D6', fontWeight: 500,
            marginBottom: 6, padding: '2px 8px', borderRadius: 6,
            background: 'rgba(111,184,214,0.1)', border: '1px solid rgba(111,184,214,0.2)',
            textDecoration: 'none',
          }}
        >
          📚 {post.series} {post.series_order !== undefined && post.series_order !== null ? `· Part ${post.series_order + 1}` : ''}
        </Link>
      )}

      {/* Title */}
      {post.title && (
        <h3
          style={{
            fontFamily: 'Playfair Display, Georgia, serif',
            fontSize: 22,
            fontWeight: 700,
            lineHeight: 1.2,
            marginBottom: 14,
            letterSpacing: '-0.2px',
          }}
        >
          <Link
            to={`/agora/space/${post.space_slug}`}
            style={{
              color: 'inherit',
              textDecoration: 'none',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = AGORA_ACCENT }}
            onMouseLeave={e => { e.currentTarget.style.color = 'inherit' }}
          >
            {post.title}
          </Link>
        </h3>
      )}

      {/* Body */}
      <div
        style={{
          fontSize: 14,
          lineHeight: 1.65,
          color: 'var(--text-primary)',
          marginBottom: hasAudio ? 18 : 14,
        }}
        onClick={
          hasAudio
            ? (e) => {
                const t = (e.target as HTMLElement).closest('button.agora-ts') as HTMLButtonElement | null
                if (!t) return
                const start = Number(t.dataset.start)
                if (!Number.isFinite(start)) return
                const endRaw = t.dataset.end
                const end = endRaw != null && endRaw !== '' ? Number(endRaw) : null
                playerRef.current?.seek(start, end ?? null)
              }
            : undefined
        }
      >
        <MarkdownRenderer content={post.content} enableTimestamps={hasAudio} />
      </div>

      {/* Audio player */}
      {hasAudio && (
        <div style={{ marginBottom: 14 }}>
          <AgoraAudioPlayer
            ref={playerRef}
            audioUrl={post.audio_url!}
            cues={post.audio_cues ?? []}
          />
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          paddingTop: 14,
          borderTop: '1px solid var(--border)',
          fontSize: 12,
          color: 'var(--text-muted)',
        }}
      >
        {/* Multi-Reactions */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {REACTION_TYPES.map(({ key, label, icon }) => {
            const count = localReactions[key] || 0
            const active = userReactions.has(key)
            return (
              <button
                key={key}
                onClick={() => toggleReaction(key)}
                title={label}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  padding: '3px 8px', borderRadius: 12, fontSize: 11,
                  background: active ? `${AGORA_ACCENT}22` : 'transparent',
                  color: active ? AGORA_ACCENT : 'var(--text-muted)',
                  border: `1px solid ${active ? AGORA_ACCENT + '55' : 'var(--border)'}`,
                  cursor: 'pointer', fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}
              >
                <span>{icon}</span>
                {count > 0 && <span>{count}</span>}
              </button>
            )
          })}
        </div>

        {/* Pin button (stewards only) */}
        {showPinButton && (
          <button
            onClick={togglePin}
            title={isPinned ? 'Unpin post' : 'Pin to top of circle'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              padding: '3px 8px', borderRadius: 12, fontSize: 11,
              background: isPinned ? '#E6C85A22' : 'transparent',
              color: isPinned ? '#E6C85A' : 'var(--text-muted)',
              border: `1px solid ${isPinned ? '#E6C85A55' : 'var(--border)'}`,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            📌 {isPinned ? 'Pinned' : 'Pin'}
          </button>
        )}

        {/* Cross-post button */}
        {!post.crosspost_original_id && spaces && spaces.length > 0 && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowCrosspost(!showCrosspost)}
              title="Cross-post to another community"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '3px 8px', borderRadius: 12, fontSize: 11,
                background: showCrosspost ? `${AGORA_ACCENT}22` : 'transparent',
                color: showCrosspost ? AGORA_ACCENT : 'var(--text-muted)',
                border: `1px solid ${showCrosspost ? AGORA_ACCENT + '55' : 'var(--border)'}`,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              ↗ Cross-post
            </button>
            {showCrosspost && (
              <div style={{
                position: 'absolute', bottom: '100%', left: 0, marginBottom: 4,
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 8, padding: 8, minWidth: 180, maxHeight: 200, overflowY: 'auto',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)', zIndex: 10,
              }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600 }}>
                  Post to:
                </div>
                {spaces.filter(s => s.slug !== post.space_slug).map(s => (
                  <div
                    key={s.slug}
                    onClick={() => !crossposting && doCrosspost(s.slug)}
                    style={{
                      padding: '5px 8px', borderRadius: 6, fontSize: 12,
                      cursor: crossposting ? 'wait' : 'pointer',
                      color: 'var(--text-primary)',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-elevated)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                  >
                    {s.title}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Comments toggle */}
        <span
          onClick={toggleReplies}
          style={{
            cursor: 'pointer',
            color: showReplies ? AGORA_ACCENT : 'var(--text-muted)',
            transition: 'color 0.15s',
          }}
        >
          {loadingReplies ? 'Loading...' : `💬 ${post.reply_count || 0} ${(post.reply_count || 0) === 1 ? 'comment' : 'comments'}`}
        </span>

        <Link
          to={`/agora/space/${post.space_slug}`}
          style={{
            marginLeft: 'auto',
            color: AGORA_ACCENT,
            textDecoration: 'none',
            fontWeight: 600,
            letterSpacing: '0.3px',
            fontSize: 11,
          }}
        >
          View in {capitalize(post.space_slug)}
        </Link>
      </div>

      {/* Inline replies */}
      {showReplies && (
        <div style={{
          marginTop: 12,
          paddingTop: 12,
          borderTop: '1px solid var(--border)',
          paddingLeft: 16,
          borderLeft: `2px solid var(--border)`,
          marginLeft: 4,
        }}>
          {replies && replies.length === 0 && (
            <div style={{ color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic', padding: '6px 0' }}>
              No replies yet.
            </div>
          )}
          <ReplyInput postId={post.id} onReplyPosted={async () => {
            const res = await fetch(`/api/agora/posts/${post.id}/replies?t=${Date.now()}`)
            const data = await res.json()
            setReplies(Array.isArray(data?.replies) ? data.replies : [])
          }} />
          {replies && replies.map(reply => (
            <div
              key={reply.id}
              style={{
                padding: '10px 0', borderBottom: '1px solid var(--border)',
                borderLeft: reply.is_solution ? '3px solid #3fb950' : 'none',
                paddingLeft: reply.is_solution ? 10 : 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                <PersonaAvatar name={reply.author} size={18} />
                <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                  {reply.author === 'dr-claude' ? 'Dr. Claude' : capitalize(reply.author)}
                </span>
                <span>· {formatTimestamp(reply.created_at)}</span>
                {reply.is_solution && (
                  <span style={{ color: '#3fb950', fontWeight: 600, fontSize: 10 }}>✓ Solution</span>
                )}
                {showPinButton && !reply.is_solution && (
                  <button
                    onClick={async () => {
                      await fetch(`/api/agora/posts/${reply.id}/solve`, { method: 'POST' })
                      const res = await fetch(`/api/agora/posts/${post.id}/replies`)
                      const data = await res.json()
                      setReplies(Array.isArray(data?.replies) ? data.replies : [])
                    }}
                    style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 4,
                      background: 'transparent', color: 'var(--text-muted)',
                      border: '1px solid var(--border)', cursor: 'pointer',
                    }}
                  >
                    ✓ Mark Solution
                  </button>
                )}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                <MarkdownRenderer content={reply.content} />
              </div>
            </div>
          ))}
        </div>
      )}
      </div>{/* end post content wrapper */}
    </article>
  )
}

function ReplyInput({ postId, onReplyPosted }: { postId: string; onReplyPosted: () => void }) {
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)

  const submit = async () => {
    if (!text.trim() || sending) return
    setSending(true)
    try {
      const resp = await fetch(`/api/agora/posts/${postId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: text.trim() }),
      })
      if (resp.ok) {
        setText('')
        onReplyPosted()
      } else {
        console.warn(`[Agora] Comment failed: ${resp.status}`)
        alert('Could not post comment — you may need to log in again.')
      }
    } catch (e) { console.warn('[Agora] Comment error:', e); alert('Could not post comment — network error.') }
    setSending(false)
  }

  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      <input
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit() } }}
        placeholder="Write a reply..."
        style={{
          flex: 1, padding: '8px 12px', fontSize: 12, borderRadius: 8,
          background: 'var(--bg-base)', color: 'var(--text-primary)',
          border: '1px solid var(--border)', fontFamily: 'inherit',
          outline: 'none',
        }}
      />
      <button
        onClick={submit}
        disabled={!text.trim() || sending}
        style={{
          padding: '8px 14px', fontSize: 12, borderRadius: 8,
          background: text.trim() ? AGORA_ACCENT : 'var(--bg-base)',
          color: text.trim() ? 'white' : 'var(--text-muted)',
          border: '1px solid var(--border)', cursor: text.trim() ? 'pointer' : 'default',
          fontFamily: 'inherit', fontWeight: 600,
        }}
      >
        {sending ? '...' : 'Reply'}
      </button>
    </div>
  )
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function formatTimestamp(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}
